import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';

import {ServerResponseHandler} from 'src/components/server-response-handler';
import {MessageContext} from 'src/contexts/message-context';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {
    REMOVE_ROLE_MODAL_WIDTH,
    RemoveRoleModal,
} from 'src/modules/manage-entitlements/edit-role/remove-role-modal';
import {getRolesWithoutRelations} from 'src/openapi/admin/apis/EntitlementsRoleApi';
import {useAppDispatch} from 'src/store/hooks';

import {BoxLoading} from '@pb/kit-component-box-loading';
import {Dialog} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import {PaginationStripe} from '@pb/kit-component-pagination';
import {Body, Cell, Head, Table} from '@pb/kit-component-table';


import {replace} from '../../entitlement-drawer.slice';

import {NotAssociatedRoleRow} from './not-associated-role-row';

import type {OpenRequest} from 'src/modules/entitlements/components/drawer/typedef';
import type {Pagination} from 'src/openapi/admin/models/Pagination';
import type {Role} from 'src/openapi/admin/models/Role';
import type {RoleList} from 'src/openapi/admin/models/RoleList';
import type {ServerResponse} from 'src/typedef';

type Props = {
    currentEntitlement: OpenRequest,
};

export const NotAssociatedRolesTable = ({currentEntitlement}: Props) => {
    const dispatch = useDispatch();
    const apiDispatch = useAppDispatch();
    const {setSuccessMessage} = useContext(MessageContext);

    const pagination = useMemo(() => (
        currentEntitlement?.notAssociatedEntitlementsDetails?.pagination || {currentPage: 1, pageSize: 10}
    ), [currentEntitlement]);

    const [roleForDeletion, setRoleForDeletion] = useState<Role | null>(null);

    const [response, setResponse] = useState<ServerResponse<RoleList> | null>(null);

    const loadNotAssociatedRoles = useCallback(async () => {
        setResponse(null);
        const serverResponse = await apiDispatch(getRolesWithoutRelations({
            pageNumber: pagination.currentPage,
            pageSize: pagination.pageSize,
        }));

        if (serverResponse.payload.success) {
            setResponse({data: serverResponse.payload.data});
        } else {
            setResponse({error: serverResponse.payload.data});
        }
    }, [pagination, setResponse, apiDispatch]);

    useEffect(() => {
        loadNotAssociatedRoles();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffectWithDeps((prevDeps, currentDeps) => {
        const {pagination: prevPagination} = prevDeps;
        const {
            loadNotAssociatedRoles: currentLoad,
            pagination: currentPagination,
        } = currentDeps;

        const paginationChanged =
            prevPagination.currentPage !== currentPagination.currentPage ||
            prevPagination.pageSize !== currentPagination.pageSize;

        if (paginationChanged) {
            currentLoad();
        }
    }, {loadNotAssociatedRoles, pagination});

    const onChangePageSize = useCallback((_currentPage: number, pageSize: number) => {
        if (currentEntitlement?.notAssociatedEntitlementsDetails) {
            dispatch(replace({
                ...currentEntitlement,
                notAssociatedEntitlementsDetails: {
                    ...currentEntitlement.notAssociatedEntitlementsDetails,
                    pagination: {currentPage: 1, pageSize},
                },
            }));
        }
    }, [dispatch, currentEntitlement]);

    const closeDeleteGrantModal = useCallback(() => {
        setRoleForDeletion(null);
    }, [setRoleForDeletion]);

    const onRemove = useCallback(() => {
        if (response?.data?.totalCount) {
            const totalPages = Math.ceil((response.data.totalCount - 1) / pagination.pageSize);

            if ((response.data.totalCount - 1) && pagination.currentPage > totalPages) {
                if (currentEntitlement?.notAssociatedEntitlementsDetails) {
                    dispatch(replace({
                        ...currentEntitlement,
                        notAssociatedEntitlementsDetails: {
                            ...currentEntitlement.notAssociatedEntitlementsDetails,
                            pagination: {currentPage: totalPages, pageSize: pagination.pageSize},
                        },
                    }));
                }
            } else {
                loadNotAssociatedRoles();
            }
        }

        setRoleForDeletion(null);
        setSuccessMessage('Role removed successfully');
    }, [
        response,
        pagination,
        setRoleForDeletion,
        setSuccessMessage,
        dispatch,
        loadNotAssociatedRoles,
        currentEntitlement,
    ]);

    const onPaginationChange = useCallback((newPagination: Pagination) => {
        if (currentEntitlement?.notAssociatedEntitlementsDetails) {
            dispatch(replace({
                ...currentEntitlement,
                notAssociatedEntitlementsDetails: {
                    ...currentEntitlement.notAssociatedEntitlementsDetails,
                    pagination: newPagination,
                },
            }));
        }
    }, [dispatch, currentEntitlement]);

    return (
        <ServerResponseHandler serverError={response?.error || null} smallView={true}>
            <Group direction="v" alignItems="start">
                <b>Roles not associated with any grants or not assigned to any user or account</b>
                <BoxLoading loading={!response}>
                    <Group direction="v" alignItems="start">
                        <Table borderedGrid={true}>
                            <Head>
                                <Cell width={56}><strong>#</strong></Cell>
                                <Cell>Role ({response?.data?.totalCount})</Cell>
                                <Cell width={50}/>
                            </Head>
                            <Body>
                                {response?.data?.items?.map((role, index) => (
                                    <NotAssociatedRoleRow
                                        key={role.id}
                                        role={role}
                                        index={index}
                                        pagination={pagination}
                                        setRoleForDeletion={setRoleForDeletion}
                                    />
                                ))}
                            </Body>
                        </Table>
                        <PaginationStripe
                            pageSize={pagination.pageSize}
                            totalCount={response?.data?.totalCount || 0}
                            currentPage={pagination.currentPage}
                            onPaginationChange={onPaginationChange}
                            onChangePaginationSize={onChangePageSize}
                            sizeSwitcherPosition="LEFT"
                            sizeSwitcherType="ONLY_PAGE_SIZE"
                            sizes={[5, 10]}
                        />
                    </Group>
                </BoxLoading>
                <Dialog
                    shown={!!roleForDeletion}
                    setShown={closeDeleteGrantModal}
                    wrapperProps={{style: {width: REMOVE_ROLE_MODAL_WIDTH}}}>
                    {roleForDeletion ? (
                        <RemoveRoleModal
                            roleId={roleForDeletion.id}
                            roleName={roleForDeletion.name}
                            onClose={closeDeleteGrantModal}
                            onDone={onRemove}
                        />
                    ) : null}
                </Dialog>
            </Group>
        </ServerResponseHandler>
    );
};
